import { apiFetch } from "./apiFetch";

export interface RealTimeStats {
	totalRequests: number;
	cacheHits: number;
	cacheHitRate: number;
	errors: number;
	tokensSaved: number;
	avgLatencyMs: number | null;
	timestamp: string;
}

export interface ChartDataPoint {
	timestamp: string;
	requests: number;
	cacheHits: number;
	hotCacheHits: number;
	semanticCacheHits: number;
	errors: number;
	avgLatencyMs: number | null;
	tokensSaved: number;
	tokensUsed: number;
}

export interface ChartData {
	timeSeries: ChartDataPoint[];
	period: "hour" | "day" | "week" | "month";
}

export interface TokenSavingsResponse {
	range: {
		from: string;
		to: string;
	};
	summary: {
		totalRequests: number;
		cacheHits: number;
		cacheHitRate: number;
		hotHits: number;
		semanticHits: number;
		tokensTotal: number;
		tokensWithoutCache: number;
		tokensSaved: number;
		savingsPercentage: number;
		avgResponseTimeMs: number | null;
		costWithoutCache: number | null;
		costActual: number | null;
		costSaved: number | null;
	};
}

export const analyticsService = {
	getRealTimeStats: async (
		projectId: string,
		modelConfigId?: string,
	): Promise<RealTimeStats> => {
		const params = modelConfigId ? `?modelConfigId=${modelConfigId}` : "";
		return apiFetch<RealTimeStats>(
			`/projects/${projectId}/metrics/realtime${params}`,
		);
	},

	getChartData: async (
		projectId: string,
		period: "hour" | "day" | "week" | "month" = "day",
		modelConfigId?: string,
	): Promise<ChartData> => {
		const params = new URLSearchParams({ period });
		if (modelConfigId) {
			params.append("modelConfigId", modelConfigId);
		}
		return apiFetch<ChartData>(
			`/projects/${projectId}/metrics/charts?${params.toString()}`,
		);
	},

	getTokenSavings: async (
		projectId: string,
		options?: {
			modelConfigId?: string;
			from?: string;
			to?: string;
		},
	): Promise<TokenSavingsResponse> => {
		const params = new URLSearchParams();
		if (options?.modelConfigId) {
			params.append("modelConfigId", options.modelConfigId);
		}
		if (options?.from) {
			params.append("from", options.from);
		}
		if (options?.to) {
			params.append("to", options.to);
		}
		const queryString = params.toString();
		return apiFetch<TokenSavingsResponse>(
			`/projects/${projectId}/metrics/token-savings${queryString ? `?${queryString}` : ""}`,
		);
	},
};

// Export standalone function for backward compatibility
export const getTokenSavings = async (
	projectId: string,
	options?: {
		modelConfigId?: string;
		from?: string;
		to?: string;
	},
): Promise<TokenSavingsResponse> => {
	return analyticsService.getTokenSavings(projectId, options);
};
